import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow } from '@mui/material';
import { useThemeMode } from '../../ThemeContext';
import { APP_URLS } from '../../helpers/url_helper';
import { dataAPI } from '../../helpers/api_service';
import AppIconBlue from '../../assets/AppIconBlue.svg';
import '../../styles/loading-animations.css';

const basicInfoFields = [
  { key: 'asset_id', label: 'Asset ID' },
  { key: 'asset_name', label: 'Asset Name' },
  { key: 'asset_type', label: 'Asset Type' },
  { key: 'manufacturer', label: 'Manufacturer' },
  { key: 'model', label: 'Model' },
  { key: 'serial_number', label: 'Serial Number' },
  { key: 'rego_number', label: 'Rego Number' },
  { key: 'rego_expiry', label: 'Rego Expiry' },
  { key: 'status', label: 'Status' },
  { key: 'condition', label: 'Condition' },
];

const usageServiceFields = [
  { key: 'odometer_km', label: 'Odometer (KM)' },
  { key: 'engine_hours', label: 'Engine Hours (Hrs)' },
  { key: 'last_service_date', label: 'Last Service Date' },
  { key: 'last_service_km', label: 'Last Service KM' },
  { key: 'last_service_hours', label: 'Last Service Hours' },
  { key: 'service_interval_km', label: 'Service Interval (KM)' },
  { key: 'service_interval_hours', label: 'Service Interval (Hrs)' },
  { key: 'service_interval_months', label: 'Service Interval (Months)' },
  { key: 'next_service_due', label: 'Next Service Due' },
  { key: 'location', label: 'Location' },
  { key: 'assigned_to', label: 'Assigned To' },
];

const getFieldDisplayValue = (asset, fieldKey, employees = []) => {
  if (!asset) return '-';
  if (fieldKey === 'assigned_to') {
    const matchedEmployee = employees.find(
      (employee) =>
        String(employee.id ?? employee.user_id ?? employee.employee_id) === String(asset.assigned_to),
    );
    return (
      asset.assigned_to_username ||
      matchedEmployee?.full_name ||
      matchedEmployee?.username ||
      matchedEmployee?.email ||
      asset.assigned_to ||
      '-'
    );
  }

  return asset[fieldKey] || '-';
};

const AssetRecordDetail = () => {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const { assetId } = useParams();
  const [asset, setAsset] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [submittedForms, setSubmittedForms] = useState([]);
  const [isSubmittedFormsLoading, setIsSubmittedFormsLoading] = useState(false);
  const [submittedFormsError, setSubmittedFormsError] = useState('');
  const [formsPage, setFormsPage] = useState(0);
  const [formsRowsPerPage, setFormsRowsPerPage] = useState(10);
  const detailViewMode = location.state?.detailView || 'all';
  const showAssetInfoSections = detailViewMode !== 'service-records';
  const showServiceRecordsSection = detailViewMode !== 'asset-overview';

  useEffect(() => {
    const fetchAssetDetail = async () => {
      try {
        setIsLoading(true);
        setError('');
        const [assetResponse, employeeResponse] = await Promise.all([
          dataAPI.getAssetRecords({ showLoading: false }),
          dataAPI.getAllEmployees({ showLoading: false }),
        ]);
        const records = Array.isArray(assetResponse?.data) ? assetResponse.data : [];
        const employeeList = Array.isArray(employeeResponse?.data) ? employeeResponse.data : [];
        setEmployees(employeeList);
        const matchedAsset = records.find((item) => String(item.id) === String(assetId));
        setAsset(matchedAsset || null);
      } catch (apiError) {
        setAsset(null);
        setError(apiError?.message || 'Failed to load asset details.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAssetDetail();
  }, [assetId]);

  useEffect(() => {
    const fetchSubmittedForms = async () => {
      const targetAssetId = String(asset?.asset_id || '').trim();
      if (!targetAssetId) {
        setSubmittedForms([]);
        return;
      }

      try {
        setIsSubmittedFormsLoading(true);
        setSubmittedFormsError('');
        const response = await dataAPI.getDigitalFormSubmissionsByDestination(
          {
            form_destination: 'selected_asset',
            asset_id: targetAssetId,
          },
          { showLoading: false },
        );
        const apiRows = Array.isArray(response?.data?.rows) ? response.data.rows : [];
        setSubmittedForms(apiRows);
      } catch (fetchError) {
        console.error('Error fetching submitted asset forms:', fetchError);
        setSubmittedForms([]);
        setSubmittedFormsError(
          fetchError?.message || 'Unable to load submitted forms right now.',
        );
      } finally {
        setIsSubmittedFormsLoading(false);
      }
    };

    fetchSubmittedForms();
  }, [asset?.asset_id]);

  useEffect(() => {
    setFormsPage(0);
  }, [submittedForms.length]);

  const formatDateTime = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    const day = String(parsed.getDate()).padStart(2, '0');
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const year = parsed.getFullYear();
    const time = parsed.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
    return `${day}-${month}-${year} ${time}`;
  };

  const paginatedSubmittedForms = submittedForms.slice(
    formsPage * formsRowsPerPage,
    formsPage * formsRowsPerPage + formsRowsPerPage,
  );

  return (
    <div style={{ padding: '24px', minHeight: '100vh', background: mode === 'dark' ? '#0f0f0f' : '#ffffff', color: mode === 'dark' ? '#ffffff' : '#111827', fontFamily: 'Urbanist, Arial, sans-serif' }}>
      <div style={{ marginBottom: '16px' }}>
        <button type="button" onClick={() => navigate(APP_URLS.ASSET_RECORD)} style={backButtonStyle(mode)}>
          <ArrowBackIcon style={{ fontSize: 18 }} />
          Back to List
        </button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginTop: '14px', flexWrap: 'wrap' }}>
          {!isLoading && !error && asset && detailViewMode !== 'service-records' && (
            <button
              type="button"
              onClick={() => navigate(`/asset-record/${asset.id}/edit`)}
              style={{
                border: 'none',
                borderRadius: '8px',
                padding: '9px 14px',
                background: '#009391',
                color: '#fff',
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              Edit Asset
            </button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 220px)' }}>
          <img
            src={AppIconBlue}
            alt="Loading"
            className="pulse-animation-icon"
            style={{ width: 46, height: 46 }}
          />
        </div>
      ) : error ? (
        <div style={{ border: `1px solid ${mode === 'dark' ? '#7f1d1d' : '#fecaca'}`, borderRadius: '12px', padding: '18px', color: mode === 'dark' ? '#fecaca' : '#b91c1c' }}>
          {error}
        </div>
      ) : !asset ? (
        <div style={{ border: `1px solid ${mode === 'dark' ? '#2a2a2a' : '#e5e7eb'}`, borderRadius: '12px', padding: '18px' }}>
          Asset record not found.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '16px' }}>
          {showAssetInfoSections ? (
            <>
              <section style={{ border: `1px solid ${mode === 'dark' ? '#2a2a2a' : '#e5e7eb'}`, borderRadius: '12px', overflow: 'hidden' }}>
                <div style={{ padding: '12px 14px', fontWeight: 700, fontSize: '16px', background: mode === 'dark' ? '#111111' : '#f9fafb', borderBottom: `1px solid ${mode === 'dark' ? '#1f2937' : '#f3f4f6'}` }}>
                  Basic Asset Information
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
                  {basicInfoFields.map((field) => (
                    <div key={field.key} style={{ padding: '12px 14px', borderBottom: `1px solid ${mode === 'dark' ? '#1f2937' : '#f3f4f6'}`, borderRight: `1px solid ${mode === 'dark' ? '#1f2937' : '#f3f4f6'}` }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: mode === 'dark' ? '#9ca3af' : '#6b7280', marginBottom: '6px' }}>
                        {field.label}
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: 500 }}>
                        {getFieldDisplayValue(asset, field.key, employees)}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section style={{ border: `1px solid ${mode === 'dark' ? '#2a2a2a' : '#e5e7eb'}`, borderRadius: '12px', overflow: 'hidden' }}>
                <div style={{ padding: '12px 14px', fontWeight: 700, fontSize: '16px', background: mode === 'dark' ? '#111111' : '#f9fafb', borderBottom: `1px solid ${mode === 'dark' ? '#1f2937' : '#f3f4f6'}` }}>
                  Service & Assignment Information
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
                  {usageServiceFields.map((field) => (
                    <div key={field.key} style={{ padding: '12px 14px', borderBottom: `1px solid ${mode === 'dark' ? '#1f2937' : '#f3f4f6'}`, borderRight: `1px solid ${mode === 'dark' ? '#1f2937' : '#f3f4f6'}` }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: mode === 'dark' ? '#9ca3af' : '#6b7280', marginBottom: '6px' }}>
                        {field.label}
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: 500 }}>
                        {getFieldDisplayValue(asset, field.key, employees)}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </>
          ) : null}

          {showServiceRecordsSection ? (
            <div style={{ marginTop: '8px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px',
              }}
            >
              <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700 }}>Asset Service Records</h2>
            </div>

            {submittedFormsError ? (
              <div
                style={{
                  padding: '24px',
                  textAlign: 'center',
                  color: mode === 'dark' ? '#f87171' : '#c0392b',
                  fontWeight: 600,
                }}
              >
                {submittedFormsError}
              </div>
            ) : isSubmittedFormsLoading ? (
              <div
                style={{
                  padding: '24px',
                  textAlign: 'center',
                  color: mode === 'dark' ? '#a0aec0' : '#7f8c8d',
                }}
              >
                Loading submitted forms...
              </div>
            ) : submittedForms.length === 0 ? (
              <div
                style={{
                  padding: '24px',
                  textAlign: 'center',
                  color: mode === 'dark' ? '#a0aec0' : '#7f8c8d',
                }}
              >
                No submitted forms found.
              </div>
            ) : (
              <Paper
                sx={{
                  backgroundColor: mode === 'dark' ? '#111111' : '#ffffff',
                  border: `1px solid ${mode === 'dark' ? '#2d2d2d' : '#eaeaea'}`,
                  borderRadius: '14px',
                  boxShadow: 'none',
                  overflow: 'hidden',
                }}
              >
                <TableContainer sx={{ maxHeight: 360, overflow: 'auto' }}>
                  <Table stickyHeader size="small" aria-label="Submitted asset forms table">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            backgroundColor: mode === 'dark' ? '#0f0f0f' : '#f4f7f7',
                            color: mode === 'dark' ? '#ffffff' : '#2c3e50',
                            fontWeight: 800,
                          }}
                        >
                          Form title
                        </TableCell>
                        <TableCell
                          sx={{
                            backgroundColor: mode === 'dark' ? '#0f0f0f' : '#f4f7f7',
                            color: mode === 'dark' ? '#ffffff' : '#2c3e50',
                            fontWeight: 800,
                          }}
                        >
                          Form description
                        </TableCell>
                        <TableCell
                          sx={{
                            backgroundColor: mode === 'dark' ? '#0f0f0f' : '#f4f7f7',
                            color: mode === 'dark' ? '#ffffff' : '#2c3e50',
                            fontWeight: 800,
                            width: 200,
                          }}
                        >
                          Submitted at
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            backgroundColor: mode === 'dark' ? '#0f0f0f' : '#f4f7f7',
                            color: mode === 'dark' ? '#ffffff' : '#2c3e50',
                            fontWeight: 800,
                            width: 140,
                          }}
                        >
                          Action
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedSubmittedForms.map((row) => (
                        <TableRow
                          key={row.id}
                          hover
                          sx={{
                            '& td': {
                              borderBottom: `1px solid ${mode === 'dark' ? '#222' : '#ececec'}`,
                            },
                          }}
                        >
                          <TableCell
                            sx={{
                              color: mode === 'dark' ? '#FFFFFF' : '#2c3e50',
                              fontWeight: 700,
                            }}
                          >
                            {row.form_title || `Form ${row.client_form_id || row.id}`}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: mode === 'dark' ? '#a0aec0' : '#64748b',
                              fontSize: '13px',
                            }}
                          >
                            {row.form_description || 'No description'}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: mode === 'dark' ? '#a0aec0' : '#7f8c8d',
                              fontSize: '12px',
                            }}
                          >
                            {formatDateTime(row.submitted_at)}
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() =>
                                navigate(APP_URLS.COMPANY_FORM_RECORD_VIEW, {
                                  state: {
                                    form: {
                                      id: row.client_form_id,
                                      title: row.form_title || 'Form Record',
                                      description: row.form_description || '',
                                    },
                                    submission: {
                                      ...row,
                                      answers: row.answers,
                                    },
                                    source: 'asset-details',
                                  },
                                })
                              }
                              sx={{
                                backgroundColor: '#009391',
                                borderRadius: '8px',
                                textTransform: 'none',
                                color: '#FFFFFF',
                                fontWeight: 600,
                                fontSize: '12px',
                                '&:hover': {
                                  backgroundColor: '#007b79',
                                },
                              }}
                            >
                              View form
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={submittedForms.length}
                  page={formsPage}
                  onPageChange={(_, newPage) => setFormsPage(newPage)}
                  rowsPerPage={formsRowsPerPage}
                  onRowsPerPageChange={(event) => {
                    setFormsRowsPerPage(parseInt(event.target.value, 10));
                    setFormsPage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  sx={{
                    color: mode === 'dark' ? '#d0d5dd' : '#344054',
                    borderTop: `1px solid ${mode === 'dark' ? '#2d2d2d' : '#eaeaea'}`,
                    backgroundColor: mode === 'dark' ? '#0a0a0a' : '#f8f9fa',
                    '& .MuiTablePagination-toolbar': {
                      minHeight: 44,
                      paddingLeft: 0,
                      paddingRight: 8,
                      justifyContent: 'flex-end',
                    },
                    '& .MuiTablePagination-spacer': {
                      flex: '0 0 0px',
                    },
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      color: mode === 'dark' ? '#d0d5dd' : '#344054',
                      margin: 0,
                    },
                    '& .MuiTablePagination-select': {
                      color: mode === 'dark' ? '#ffffff' : '#1f2937',
                    },
                    '& .MuiSvgIcon-root': {
                      color: mode === 'dark' ? '#d0d5dd' : '#344054',
                    },
                    '& .MuiTablePagination-actions': {
                      marginLeft: 8,
                    },
                  }}
                />
              </Paper>
            )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

const backButtonStyle = (mode) => ({
  border: `1px solid ${mode === 'dark' ? '#4a4a4a' : '#d0d5dd'}`,
  background: 'transparent',
  color: mode === 'dark' ? '#fff' : '#111',
  borderRadius: 8,
  padding: '7px 12px',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
});

export default AssetRecordDetail;
